import React from "react";
import {
  Avatar as MuiAvatar,
  AvatarProps as MuiAvatarProps,
  Badge,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Avatar 크기 정의 (Figma 기준)
export type AvatarSize = "18px" | "24px" | "32px" | "40px";

// Avatar 모양 정의 (Figma 기준)
export type AvatarVariant = "circular" | "rounded" | "square";

// Avatar 내용 타입 정의 (Figma 기준)
export type AvatarContent = "text" | "icon" | "image";

/**
 * 사용자 아바타 props — `content` 에 따라 이니셜·아이콘·이미지 중 하나로 렌더링.
 *
 * `showBadge` + `animatedBadge` 조합으로 상태 펄스 표시 가능 (예: 온라인 표시).
 */
export interface AvatarProps extends Omit<MuiAvatarProps, "size"> {
  /**
   * Avatar의 크기 (Figma 기준)
   * @default '40px'
   */
  size?: AvatarSize;

  /**
   * Avatar의 모양 (Figma 기준)
   * @default 'circular'
   */
  variant?: AvatarVariant;

  /**
   * Avatar의 내용 타입
   * @default 'text'
   */
  content?: AvatarContent;

  /**
   * 사용자 이름 (이니셜 표시용)
   */
  name?: string;

  /**
   * 프로필 이미지 URL
   */
  src?: string;

  /**
   * 이미지 로드 실패 시 대체 텍스트
   */
  alt?: string;

  /**
   * 아이콘 컴포넌트 (content가 'icon'일 때)
   */
  icon?: React.ReactNode;

  /**
   * Badge 표시 여부
   * @default false
   */
  showBadge?: boolean;

  /**
   * Badge 색상
   * @default '#2e7d32' (성공 색상)
   */
  badgeColor?: string;

  /**
   * 클릭 가능한지 여부
   * @default false
   */
  clickable?: boolean;

  /**
   * 클릭 핸들러
   */
  onClick?: () => void;

  /**
   * Badge 애니메이션 효과 (리플 효과)
   * @default false
   */
  animatedBadge?: boolean;
}

// 크기별 스타일 정의 (Figma 기준)
const sizeStyles = {
  "18px": {
    width: 18,
    height: 18,
    fontSize: "10px",
  },
  "24px": {
    width: 24,
    height: 24,
    fontSize: "12px",
  },
  "32px": {
    width: 32,
    height: 32,
    fontSize: "20px",
  },
  "40px": {
    width: 40,
    height: 40,
    fontSize: "20px",
  },
};

// 모양별 border radius 정의 (Figma 기준)
const variantStyles = {
  circular: "50%",
  rounded: "4px",
  square: "0px",
};

// Styled Avatar 컴포넌트
const StyledAvatar = styled(MuiAvatar, {
  shouldForwardProp: (prop) =>
    !["variant", "clickable"].includes(prop as string),
})<{
  variant: AvatarVariant;
  clickable: boolean;
}>(({ theme, variant, clickable }) => ({
  borderRadius: variantStyles[variant],
  backgroundColor: "#bdbdbd", // Figma 기준 기본 색상
  color: "#ffffff", // Figma 기준 텍스트 색상
  fontFamily: "Pretendard", // Figma 기준 폰트
  fontWeight: 400,
  cursor: clickable ? "pointer" : "default",
  transition: theme.transitions.create(["transform", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),

  "&:hover": clickable
    ? {
        transform: "scale(1.05)",
        boxShadow: theme.shadows[4],
      }
    : {},

  "&:active": clickable
    ? {
        transform: "scale(0.95)",
      }
    : {},

  "&:focus-visible": clickable
    ? {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      }
    : {},
}));

// Styled Badge 컴포넌트 - MUI 예제 기반으로 개선
const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#2e7d32", // 기본 배지 색상
    color: "#2e7d32",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    borderRadius: "50%",
    minWidth: "12px",
    height: "12px",
    padding: 0,
    right: 4,
    bottom: 4,
    top: "auto",
    // 애니메이션 효과 (선택적)
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  // 리플 애니메이션 (선택적)
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

/**
 * Avatar 컴포넌트
 *
 * 사용자 프로필 이미지, 이니셜, 또는 아이콘을 표시하는 컴포넌트입니다.
 * Figma 디자인 시스템을 기반으로 구현되었습니다.
 *
 * @example
 * ```tsx
 * // 기본 사용법 (이니셜)
 * <Avatar name="홍길동" />
 *
 * // 이미지와 함께
 * <Avatar src="/profile.jpg" alt="프로필" content="image" />
 *
 * // 아이콘과 함께
 * <Avatar icon={<PersonIcon />} content="icon" />
 *
 * // 크기와 모양 지정
 * <Avatar name="김철수" size="32px" variant="rounded" />
 *
 * // Badge와 함께
 * <Avatar name="이영희" showBadge badgeColor="#2e7d32" />
 *
 * // 클릭 가능한 아바타
 * <Avatar name="박민수" clickable onClick={() => console.log('클릭됨')} />
 * ```
 */
export const Avatar: React.FC<AvatarProps> = ({
  size = "40px",
  variant = "circular",
  content = "text",
  name,
  src,
  alt,
  icon,
  showBadge = false,
  badgeColor = "#2e7d32",
  clickable = false,
  onClick,
  animatedBadge = false,
  ...props
}) => {
  const theme = useTheme();
  // 이니셜 생성 함수
  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // 크기 스타일 적용
  const sizeStyle = sizeStyles[size];

  // 클릭 핸들러
  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  // 키보드 접근성
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (clickable && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      handleClick();
    }
  };

  // Avatar 내용 렌더링
  const renderAvatarContent = () => {
    switch (content) {
      case "image":
        return null; // src가 있으면 MUI Avatar가 자동으로 이미지 표시
      case "icon":
        return icon;
      case "text":
      default:
        return name ? getInitials(name) : null;
    }
  };

  const avatarElement = (
    <StyledAvatar
      src={content === "image" ? src : undefined}
      alt={alt || name}
      variant={variant}
      clickable={clickable}
      onClick={clickable ? handleClick : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? "button" : undefined}
      aria-label={clickable ? `프로필 보기: ${name || alt}` : undefined}
      sx={{
        ...sizeStyle,
        ...props.sx,
      }}
      {...props}
    >
      {renderAvatarContent()}
    </StyledAvatar>
  );

  // Badge가 있는 경우 Badge로 감싸기
  if (showBadge) {
    return (
      <StyledBadge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        variant="dot"
        sx={{
          "& .MuiBadge-badge": {
            backgroundColor: badgeColor,
            color: badgeColor,
            boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
            ...(animatedBadge && {
              "&::after": {
                animation: "ripple 1.2s infinite ease-in-out",
              },
            }),
          },
        }}
      >
        {avatarElement}
      </StyledBadge>
    );
  }

  return avatarElement;
};

export default Avatar;
